import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ArchitectureEditor = ({ data, onChange, token }) => {
  const [uploading, setUploading] = useState({});

  const updateProperty = (index, field, value) => {
    const updated = { ...data };
    updated.properties[index] = { ...updated.properties[index], [field]: value };
    onChange(updated);
  };

  const handleImageUpload = async (propertyIndex, type = 'image', galleryIndex = null) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const key = `${propertyIndex}-${type}-${galleryIndex}`;
      setUploading(prev => ({ ...prev, [key]: true }));

      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/upload/image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const imageUrl = `${BACKEND_URL}${response.data.url}`;
        
        if (type === 'gallery') {
          const updated = { ...data };
          if (galleryIndex !== null) {
            updated.properties[propertyIndex].gallery[galleryIndex] = imageUrl;
          } else {
            updated.properties[propertyIndex].gallery.push(imageUrl);
          }
          onChange(updated);
        } else {
          updateProperty(propertyIndex, 'image', imageUrl);
        }
        
        toast.success('Image uploaded!');
      } catch (error) {
        toast.error('Upload failed');
      } finally {
        setUploading(prev => ({ ...prev, [key]: false }));
      }
    };
    
    input.click();
  };

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-lg p-6">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Properties</h2>
        <Input
          value={data.title}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
          placeholder="Section Title"
        />
      </div>

      {data.properties.map((property, index) => (
        <div key={property.id} className="bg-white rounded-lg p-6 space-y-6">
          <h3 className="text-xl font-bold text-slate-900">{property.name}</h3>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Property Name</label>
              <Input value={property.name} onChange={(e) => updateProperty(index, 'name', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Tagline</label>
              <Input value={property.tagline} onChange={(e) => updateProperty(index, 'tagline', e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <Textarea value={property.description} onChange={(e) => updateProperty(index, 'description', e.target.value)} rows={3} />
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Bedrooms</label>
              <Input type="number" value={property.specs.bedrooms} onChange={(e) => updateProperty(index, 'specs', { ...property.specs, bedrooms: parseInt(e.target.value) })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bathrooms</label>
              <Input type="number" value={property.specs.bathrooms} onChange={(e) => updateProperty(index, 'specs', { ...property.specs, bathrooms: parseInt(e.target.value) })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Area</label>
              <Input value={property.specs.area} onChange={(e) => updateProperty(index, 'specs', { ...property.specs, area: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Capacity</label>
              <Input value={property.specs.capacity} onChange={(e) => updateProperty(index, 'specs', { ...property.specs, capacity: e.target.value })} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Main Image</label>
            <div className="flex gap-2">
              <Input value={property.image} onChange={(e) => updateProperty(index, 'image', e.target.value)} className="flex-1" />
              <Button variant="outline" onClick={() => handleImageUpload(index, 'image')}>
                <Upload className="w-4 h-4" />
              </Button>
            </div>
            {property.image && <img src={property.image} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Gallery Images</label>
            <div className="space-y-2">
              {property.gallery.map((img, imgIndex) => (
                <div key={imgIndex} className="flex gap-2 items-center">
                  <Input value={img} readOnly className="flex-1" />
                  <Button variant="outline" size="sm" onClick={() => handleImageUpload(index, 'gallery', imgIndex)}>
                    <Upload className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    const updated = { ...data };
                    updated.properties[index].gallery.splice(imgIndex, 1);
                    onChange(updated);
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={() => handleImageUpload(index, 'gallery')} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Add Gallery Image
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ArchitectureEditor;
